<template>
  <div
    :id="id"
    :class="classes"
    :tabindex="effectiveFocusOnMounted ? -1 : undefined"
    class="flex items-start gap-2 border-l-4 px-4 py-2 focus:outline-none"
  >
    <component
      :is="icon"
      :class="iconClasses"
      :size="24"
      aria-hidden="true"
      class="shrink-0"
    />
    <div class="flex flex-col gap-2">
      <component
        :is="titleElement"
        :id="titleId"
        :class="{ 'hidden': hideTitleElement }"
        :aria-hidden="true"
        class="body-sm font-semibold text-gray-800"
        v-text="headingText"
      />
      <div ref="bodyRef">
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { IconAlertCircleFilled, IconAlertTriangleFilled, IconInfoCircleFilled, IconCircleCheckFilled } from '@tabler/icons-vue'

import { useAnnouncer } from '@js/composables/useAnnouncer'

defineOptions({
  name: 'BaseInlineNotification',
})

const props = withDefaults(
  defineProps<{
    id?: string
    titleId?: string
    headingText: string
    focusOnMounted?: boolean
    hideTitleElement?: boolean
    type: 'success' | 'error' | 'warning' | 'info'
    titleElement?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  }>(),
  {
    titleElement: 'h2',
    hideTitleElement: false,
    id: 'inline-notification',
    titleId: 'inline-notification-title',
  },
)

const effectiveFocusOnMounted = computed(() =>
  props.focusOnMounted ?? (props.type === 'error' || props.type === 'warning'),
)

const classes = computed(() => {
  return {
    'error': 'border-red bg-red-light text-gray-800',
    'success': 'border-green bg-green-light text-gray-800',
    'info': 'border-blue bg-blue-light text-gray-800',
    'warning': 'border-yellow bg-yellow-light text-gray-800',
  }[props.type]
})

const icon = computed(() => {
  return {
    'error': IconAlertCircleFilled,
    'warning': IconAlertTriangleFilled,
    'info': IconInfoCircleFilled,
    'success': IconCircleCheckFilled,
  }[props.type]
})

const iconClasses = computed(() => {
  return {
    'error': 'text-red',
    'warning': 'text-yellow',
    'info': 'text-blue',
    'success': 'text-green',
  }[props.type]
})

const { announce } = useAnnouncer()
const bodyRef = ref<HTMLDivElement | null>(null)
let cancelAnnouncement: (() => void) | null = null

onMounted(async () => {
  await nextTick()

  const bodyText = bodyRef.value?.textContent?.trim() ?? ''
  const message = [props.headingText, bodyText].filter(Boolean).join('. ')
  const politeness = props.type === 'error' || props.type === 'warning' ? 'assertive' : 'polite'

  cancelAnnouncement = announce(message, politeness)

  if (effectiveFocusOnMounted.value) {
    document.getElementById(props.id)?.focus()
  }
})

onBeforeUnmount(() => {
  cancelAnnouncement?.()
})
</script>
